import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-[var(--pf-accent)] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">staqblox</span>
          </div>

          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2" data-testid="login-heading">
            Sign in
          </h1>
          <p className="text-[var(--pf-text-secondary)] mb-8">
            Enter your credentials to access your workspace.
          </p>

          {error && (
            <div className="bg-[var(--pf-error-bg)] border border-[var(--pf-error-border)] text-red-700 dark:text-red-400 px-4 py-3 mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-11 font-sans"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none font-medium transition-colors duration-150"
            >
              {loading ? "Signing in..." : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[var(--pf-text-secondary)]">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[var(--pf-surface)] items-center justify-center border-l border-[var(--pf-border)]">
        <div className="max-w-md p-12">
          <p className="font-heading text-2xl font-bold tracking-tight mb-6">
            Turn ideas into execution-ready prompts
          </p>
          <div className="space-y-4">
            <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-2">You type</p>
              <p className="text-sm text-[var(--pf-text)] font-mono">"Invoice tool for freelancers with payment tracking"</p>
            </div>
            <div className="flex justify-center">
              <ArrowRight className="w-4 h-4 text-[var(--pf-accent)]" />
            </div>
            <div className="border border-[var(--pf-accent)] bg-[var(--pf-bg)] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-accent)] mb-2">You get</p>
              <p className="text-sm text-[var(--pf-text)]">50+ structured prompt blocks across 5 build phases — scored, refined, and ready to paste into any AI build platform.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
